import Appsignal from "@appsignal/javascript";
import { plugin as windowEventsPlugin } from "@appsignal/plugin-window-events";

let appsignal: Appsignal | null = null;

/**
 * Initialize the AppSignal frontend agent with the RUNTIME environment, not
 * build-time env: weft-app's Docker image is promoted staging → prod
 * unmodified ("promote, never rebuild"), so a VITE_ var baked at build time
 * would print "staging" in production. The server serves `environment` +
 * `revision` via the `public_config` Inertia shared prop
 * (ApplicationController#share_public_config).
 *
 * The frontend API key is a build-time Vite env var:
 *   VITE_APPSIGNAL_FRONTEND_KEY — AppSignal dashboard: Push and deploy >
 *   Front-end error monitoring.
 *
 * Note: AppSignal frontend keys are APP-scoped (one key per app, no
 * per-environment separation in the JS agent — `environment` is not a client
 * option). Because weft-app's image is built once (staging) and promoted to
 * prod, the same baked key serves both environments, so frontend errors
 * attribute to the app, not the environment. `environment` here only gates
 * enablement; `revision` distinguishes deploys within the app.
 *
 * In development, AppSignal is opt-in to avoid accidental telemetry from
 * local work. Enable explicitly with VITE_APPSIGNAL_FRONTEND_ACTIVE=true.
 */
export function initializeAppsignal(config: {
  environment: string;
  revision: string;
}): Appsignal | null {
  const frontendKey = import.meta.env.VITE_APPSIGNAL_FRONTEND_KEY;
  const frontendActiveFlag =
    import.meta.env.VITE_APPSIGNAL_FRONTEND_ACTIVE === "true";
  const isProductionLikeEnv =
    config.environment === "staging" || config.environment === "production";
  const appsignalEnabled =
    Boolean(frontendKey) && (frontendActiveFlag || isProductionLikeEnv);

  if (!appsignalEnabled) {
    appsignal = null;
    return appsignal;
  }

  appsignal = new Appsignal({
    key: frontendKey,
    revision: config.revision || "unknown",
  });

  // Enable uncaught exception and unhandled promise rejection tracking
  appsignal.use(windowEventsPlugin());
  return appsignal;
}

export { appsignal };
